import { Box, Tooltip, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import type { FC } from "react";
import {
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  type TooltipProps,
} from "recharts";

const CATEGORY_PALETTE = [
  "#05204c",
  "#0077b6",
  "#00b4d8",
  "#48cae4",
  "#29bd29",
  "#ee923d",
  "#cb3215",
  "#9747ff",
  "#68686f",
];

function colorForCategoryId(categoryId: string) {
  let h = 0;
  for (let i = 0; i < categoryId.length; i++) {
    h = (h + categoryId.charCodeAt(i) * (i + 1)) % CATEGORY_PALETTE.length;
  }
  return CATEGORY_PALETTE[h]!;
}

/** Inner-ring (subcategory) slices: same hue family as category, stepped opacity for separation. */
function subSliceColor(categoryId: string, shadeIndex: number) {
  const base = colorForCategoryId(categoryId);
  const t = 0.45 + (shadeIndex % 6) * 0.08;
  return alpha(base, Math.min(0.93, t));
}

export interface CategorySubcategoryRow {
  categoryId: string;
  categoryName: string;
  subCategoryName: string;
  count: number;
}

interface KpiCategorySubcategoryPieProps {
  title: string;
  rows: CategorySubcategoryRow[];
}

type CategorySlice = {
  name: string;
  value: number;
  categoryId: string;
  layer: "category";
};

type SubSlice = {
  id: string;
  name: string;
  value: number;
  categoryId: string;
  layer: "sub";
  shadeIndex: number;
};

function NestedTooltip({
  active,
  payload,
  total,
}: TooltipProps<number, string> & { total: number }) {
  if (!active || !payload?.length) return null;
  const p = payload[0];
  const row = p.payload as CategorySlice | SubSlice;
  const value = Number(p.value ?? 0);
  const pct = total > 0 ? ((value / total) * 100).toFixed(1) : "0";
  const isCategory = row.layer === "category";
  const label = row.name;
  return (
    <Box
      sx={{
        px: 1.25,
        py: 0.75,
        bgcolor: "background.paper",
        border: 1,
        borderColor: "divider",
        borderRadius: 1,
        boxShadow: 1,
        maxWidth: 220,
      }}
    >
      <Typography
        variant="caption"
        color="text.secondary"
        display="block"
        sx={{ fontSize: "0.65rem", mb: 0.25 }}
      >
        {isCategory ? "קטגוריה" : "תת־קטגוריה"}
      </Typography>
      <Tooltip title={label} placement="top" enterDelay={400}>
        <Typography
          variant="caption"
          display="block"
          sx={{
            fontWeight: 600,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {label}
        </Typography>
      </Tooltip>
      <Typography variant="caption" color="text.secondary" component="span">
        {value} ({pct}%)
      </Typography>
    </Box>
  );
}

type CategoryGroup = {
  categoryId: string;
  categoryName: string;
  total: number;
  subs: { subCategoryName: string; count: number }[];
};

function buildCategoryHierarchy(rows: CategorySubcategoryRow[]): CategoryGroup[] {
  const map = new Map<
    string,
    { categoryName: string; subs: Map<string, number> }
  >();

  for (const r of rows) {
    if (r.count <= 0) continue;
    let g = map.get(r.categoryId);
    if (!g) {
      g = { categoryName: r.categoryName, subs: new Map() };
      map.set(r.categoryId, g);
    }
    const key = r.subCategoryName;
    g.subs.set(key, (g.subs.get(key) ?? 0) + r.count);
  }

  const groups: CategoryGroup[] = [];
  for (const [categoryId, g] of map) {
    const subs = [...g.subs.entries()].map(([subCategoryName, count]) => ({
      subCategoryName,
      count,
    }));
    const total = subs.reduce((s, x) => s + x.count, 0);
    subs.sort((a, b) => b.count - a.count);
    groups.push({
      categoryId,
      categoryName: g.categoryName,
      total,
      subs,
    });
  }

  groups.sort((a, b) => b.total - a.total);
  return groups;
}

const indexListSx = {
  flex: 1,
  minWidth: 0,
  maxHeight: 300,
  overflow: "auto",
  pr: 0.5,
  borderRadius: 1,
  border: "1px solid",
  borderColor: "divider",
  bgcolor: "action.hover",
  py: 0.5,
} as const;

const indexTooltipProps = {
  placement: "top-start" as const,
  enterDelay: 300,
  slotProps: {
    popper: {
      popperOptions: {
        strategy: "fixed" as const,
        modifiers: [
          { name: "offset", options: { offset: [0, 6] } },
          { name: "preventOverflow", options: { padding: 8 } },
        ],
      },
    },
  },
};

export const KpiCategorySubcategoryPie: FC<KpiCategorySubcategoryPieProps> = ({
  title,
  rows,
}) => {
  const hierarchy = buildCategoryHierarchy(rows);

  const categoryData: CategorySlice[] = hierarchy.map((cat) => ({
    name: cat.categoryName,
    value: cat.total,
    categoryId: cat.categoryId,
    layer: "category",
  }));

  const subData: SubSlice[] = hierarchy.flatMap((cat) =>
    cat.subs.map((sub, subIdx) => ({
      id: `${cat.categoryId}\0${sub.subCategoryName}`,
      name: sub.subCategoryName,
      value: sub.count,
      categoryId: cat.categoryId,
      layer: "sub" as const,
      shadeIndex: subIdx,
    })),
  );

  const total = categoryData.reduce((s, d) => s + d.value, 0);
  const hasData = categoryData.length > 0;

  return (
    <Box
      sx={{
        width: "100%",
        minWidth: 0,
        display: "flex",
        flexDirection: "column",
        minHeight: hasData ? 340 : "auto",
      }}
    >
      <Tooltip title={title} placement="top" enterDelay={400}>
        <Box sx={{ width: "100%", mb: 1, px: 0.5 }}>
          <Typography
            sx={{
              fontSize: "0.8125rem",
              fontWeight: 600,
              color: "text.primary",
              textAlign: "center",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {title}
          </Typography>
        </Box>
      </Tooltip>

      {!hasData ? (
        <Box
          sx={{
            py: 1.5,
            px: 1,
            borderRadius: 1,
            bgcolor: "action.hover",
            border: "1px solid",
            borderColor: "divider",
            textAlign: "center",
          }}
        >
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block" }}
          >
            אין נתונים
          </Typography>
        </Box>
      ) : (
        <Box
          sx={{
            display: "grid",
            gridTemplateRows: "auto auto",
            gap: 1,
            minWidth: 0,
          }}
        >
          <Box
            sx={{
              width: "100%",
              height: { xs: 300, md: 340 },
              alignSelf: "center",
            }}
          >
            <ResponsiveContainer width="100%" height="100%">
              <PieChart margin={{ top: 4, right: 4, bottom: 4, left: 4 }}>
                {/*
                  Outer ring = categories; inner ring = subcategories.
                  subData is grouped by category to match categoryData so arcs align.
                  Draw category band first, then sub band on top (center).
                */}
                <Pie
                  data={categoryData}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  innerRadius={78}
                  outerRadius={122}
                  paddingAngle={0}
                  stroke="#fff"
                  strokeWidth={1}
                  isAnimationActive={false}
                  label={false}
                >
                  {categoryData.map((d, i) => (
                    <Cell
                      key={`cat-${d.categoryId}-${i}`}
                      fill={colorForCategoryId(d.categoryId)}
                    />
                  ))}
                </Pie>
                <Pie
                  data={subData}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  innerRadius={48}
                  outerRadius={76}
                  paddingAngle={0}
                  stroke="#fff"
                  strokeWidth={1}
                  isAnimationActive={false}
                  label={false}
                >
                  {subData.map((d, i) => (
                    <Cell
                      key={`sub-${d.id}-${i}`}
                      fill={subSliceColor(d.categoryId, d.shadeIndex)}
                    />
                  ))}
                </Pie>
                <RechartsTooltip
                  content={<NestedTooltip total={total} />}
                  wrapperStyle={{ outline: "none" }}
                />
              </PieChart>
            </ResponsiveContainer>
          </Box>

          <Box sx={indexListSx}>
            {hierarchy.map((cat) => {
              const catColor = colorForCategoryId(cat.categoryId);
              const catPct =
                total > 0 ? ((cat.total / total) * 100).toFixed(0) : "0";
              const railColor = alpha(catColor, 0.5);
              return (
                <Box key={cat.categoryId}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      px: 1,
                      py: 0.5,
                      borderBottom: "1px solid",
                      borderColor: "divider",
                    }}
                  >
                    <Box
                      sx={{
                        width: 10,
                        height: 10,
                        borderRadius: "50%",
                        flexShrink: 0,
                        bgcolor: catColor,
                      }}
                    />
                    <Tooltip title={cat.categoryName} {...indexTooltipProps}>
                      <Box
                        component="span"
                        sx={{
                          flex: 1,
                          minWidth: 0,
                          display: "block",
                        }}
                      >
                        <Typography
                          variant="caption"
                          noWrap
                          component="span"
                          display="block"
                          sx={{ fontWeight: 700 }}
                        >
                          {cat.categoryName}
                        </Typography>
                      </Box>
                    </Tooltip>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        flexShrink: 0,
                        fontVariantNumeric: "tabular-nums",
                      }}
                    >
                      {cat.total} ({catPct}%)
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      marginInlineStart: 1.25,
                      paddingInlineStart: 2.5,
                      paddingTop: 0.35,
                      paddingBottom: 0.35,
                      borderInlineStart: "3px solid",
                      borderColor: railColor,
                    }}
                  >
                    {cat.subs.map((sub, j) => {
                      const pct =
                        total > 0
                          ? ((sub.count / total) * 100).toFixed(0)
                          : "0";
                      return (
                        <Box
                          key={`${cat.categoryId}-${sub.subCategoryName}-${j}`}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            py: 0.4,
                          }}
                        >
                          <Box
                            sx={{
                              width: 6,
                              height: 6,
                              borderRadius: "50%",
                              flexShrink: 0,
                              bgcolor: subSliceColor(cat.categoryId, j),
                              boxShadow: `inset 0 0 0 1px ${alpha("#fff", 0.35)}`,
                            }}
                          />
                          <Tooltip title={sub.subCategoryName} {...indexTooltipProps}>
                            <Box
                              component="span"
                              sx={{
                                flex: 1,
                                minWidth: 0,
                                display: "block",
                              }}
                            >
                              <Typography
                                variant="caption"
                                noWrap
                                component="span"
                                display="block"
                                sx={{
                                  fontSize: "0.7rem",
                                  color: "text.secondary",
                                }}
                              >
                                {sub.subCategoryName}
                              </Typography>
                            </Box>
                          </Tooltip>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                              flexShrink: 0,
                              fontVariantNumeric: "tabular-nums",
                              fontSize: "0.7rem",
                            }}
                          >
                            {sub.count} ({pct}%)
                          </Typography>
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>
      )}
    </Box>
  );
};
